import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axiosInstance from "../../config/axiosApi/axiosApi.jsx";
import APIs from "../../Constants/apis.js";
import Preloader from "../../components/CustomComponents/preLoad/preLoader.js";
import showErrorToast, { showSuccessToast } from "../../components/CustomComponents/toast/toast.js";
import useDarkMode from "../../hooks/useDarkMode.js";
import useThemeColor from "../../hooks/useThemeColor.js";
import "../compnay/ManageCompany.css";

const formatMoney = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const ManageRiderCreditLimits = () => {
  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const [loading, setLoading] = useState(false);
  const [savingStar, setSavingStar] = useState(null);
  const [rows, setRows] = useState([]);

  const fieldStyle = useMemo(
    () => ({
      borderRadius: "10px",
      border: isDarkMode ? "1px solid #404040" : "1px solid #D9D9D9",
      background: isDarkMode ? "#2d3436" : "#F9F9F9",
      color: isDarkMode ? "#ffffff" : "#495057",
      height: "44px",
    }),
    [isDarkMode]
  );

  const cardStyle = useMemo(
    () => ({
      background: isDarkMode ? "rgba(45, 52, 54, 0.55)" : "#fff",
      border: isDarkMode ? "1px solid rgba(255,255,255,0.08)" : "1px solid #eceff1",
      borderRadius: "12px",
      boxShadow: isDarkMode ? "none" : "0 1px 4px rgba(0,0,0,0.04)",
      height: "100%",
    }),
    [isDarkMode]
  );

  const fetchLimits = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(APIs.GET_RIDER_CREDIT_LIMITS);
      if (response.data.status) {
        const data = Array.isArray(response.data.data) ? response.data.data : [];
        setRows(
          data
            .map((item) => ({
              starLevel: item.starLevel ?? item.StarLevel,
              creditLimit: item.creditLimit ?? item.CreditLimit,
              draftLimit: String(item.creditLimit ?? item.CreditLimit ?? 0),
              updatedAt: item.updatedAt ?? item.UpdatedAt,
              updatedBy: item.updatedBy ?? item.UpdatedBy,
            }))
            .sort((a, b) => Number(a.starLevel) - Number(b.starLevel))
        );
      } else {
        showErrorToast(response.data.message || "Failed to load credit limits");
      }
    } catch {
      showErrorToast("Failed to load credit limits");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLimits();
  }, [fetchLimits]);

  const onDraftChange = (starLevel, value) => {
    setRows((prev) =>
      prev.map((r) => (r.starLevel === starLevel ? { ...r, draftLimit: value } : r))
    );
  };

  const isDirty = (row) => {
    const parsed = Number(row.draftLimit);
    if (Number.isNaN(parsed)) return true;
    return parsed !== Number(row.creditLimit);
  };

  const saveRow = async (row) => {
    const parsed = Number(row.draftLimit);
    if (Number.isNaN(parsed) || parsed < 0) {
      showErrorToast("Enter a valid non-negative credit limit");
      return;
    }

    try {
      setSavingStar(row.starLevel);
      const response = await axiosInstance.post(APIs.POST_UPSERT_RIDER_CREDIT_LIMIT, {
        starLevel: row.starLevel,
        creditLimit: parsed,
      });
      if (response.data.status) {
        showSuccessToast(`Star ${row.starLevel} credit limit saved`);
        await fetchLimits();
      } else {
        showErrorToast(response.data.message || "Failed to save credit limit");
      }
    } catch {
      showErrorToast("Failed to save credit limit");
    } finally {
      setSavingStar(null);
    }
  };

  return (
    <div>
      {loading && <Preloader />}

      <div className="d-flex align-items-center mb-3">
        <div>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/">Home</Link>
            </li>
            <li className="breadcrumb-item active">Rider Credit Limits</li>
          </ol>
          <h1 className="page-header mb-0">COD Credit Limits by Star</h1>
          <p className="text-muted mb-0 mt-1">
            Riders cannot accept new orders when outstanding COD reaches their star limit.
          </p>
        </div>
      </div>

      <div
        className="card border-0 mb-4"
        style={{
          background: `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`,
          borderRadius: "12px",
          boxShadow: `0 4px 20px ${themeColor}40`,
        }}
      >
        <div className="card-body p-3 d-flex flex-wrap align-items-center gap-3 text-white">
          <div
            className="d-flex align-items-center justify-content-center rounded-circle"
            style={{
              width: 44,
              height: 44,
              background: "rgba(255,255,255,0.2)",
              flexShrink: 0,
            }}
          >
            <i className="fa fa-star" />
          </div>
          <div className="flex-grow-1">
            <div className="fw-semibold">How credit limits work</div>
            <div style={{ opacity: 0.92, fontSize: "0.9rem" }}>
              Higher star levels typically allow a larger outstanding COD balance before the rider is blocked from new orders.
              Save each level individually after editing.
            </div>
          </div>
          <button
            type="button"
            className="btn btn-sm"
            onClick={fetchLimits}
            disabled={loading}
            style={{
              background: "rgba(255,255,255,0.2)",
              border: "1px solid rgba(255,255,255,0.35)",
              color: "#fff",
              borderRadius: "8px",
              fontWeight: 600,
              padding: "8px 14px",
            }}
          >
            <i className="fa fa-sync-alt me-1" />
            Refresh
          </button>
        </div>
      </div>

      <div className="card border-0">
        <div className="card-body p-3 p-md-4">
          {rows.length > 0 ? (
            <div className="row g-3">
              {rows.map((row) => {
                const dirty = isDirty(row);
                const saving = savingStar === row.starLevel;
                return (
                  <div className="col-12 col-md-6 col-xl-4" key={row.starLevel}>
                    <div className="p-3" style={cardStyle}>
                      <div className="d-flex align-items-start justify-content-between mb-3">
                        <div>
                          <div className="text-muted small text-uppercase mb-1" style={{ letterSpacing: "0.04em" }}>
                            Star level
                          </div>
                          <div className="d-flex align-items-center gap-2">
                            <span style={{ color: "#f0ad4e", fontSize: "1.15rem", letterSpacing: "1px" }}>
                              {"★".repeat(Math.max(0, Number(row.starLevel) || 0))}
                            </span>
                            <span className="fw-semibold">{row.starLevel} / 5</span>
                          </div>
                        </div>
                        {dirty && (
                          <span className="badge bg-warning text-dark">Unsaved</span>
                        )}
                      </div>

                      <label className="form-label mb-1">Credit limit</label>
                      <div className="input-group mb-2">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="form-control"
                          value={row.draftLimit}
                          onChange={(e) => onDraftChange(row.starLevel, e.target.value)}
                          style={fieldStyle}
                        />
                      </div>
                      <div className="small text-muted mb-3">
                        Current saved: <strong>{formatMoney(row.creditLimit)}</strong>
                      </div>

                      <div className="small text-muted mb-3" style={{ minHeight: 40 }}>
                        <div>
                          Updated:{" "}
                          {row.updatedAt ? new Date(row.updatedAt).toLocaleString() : "—"}
                        </div>
                        <div>By: {row.updatedBy || "—"}</div>
                      </div>

                      <button
                        type="button"
                        className="btn w-100"
                        disabled={saving || !dirty}
                        onClick={() => saveRow(row)}
                        style={{
                          backgroundColor: dirty ? themeColor : (isDarkMode ? "#3d4547" : "#c4c4c4"),
                          background: dirty
                            ? `linear-gradient(135deg, ${themeColor} 0%, ${themeColor}dd 100%)`
                            : undefined,
                          border: "none",
                          color: dirty || isDarkMode ? "#fff" : "#666",
                          borderRadius: "8px",
                          padding: "10px 16px",
                          fontWeight: 600,
                          boxShadow: dirty ? `0 2px 8px ${themeColor}40` : "none",
                          opacity: saving || !dirty ? 0.75 : 1,
                        }}
                      >
                        {saving ? "Saving..." : dirty ? "Save limit" : "Saved"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-5">
              <div className="mb-2" style={{ fontSize: "2rem", opacity: 0.35 }}>
                <i className="fa fa-star" />
              </div>
              <div style={{ fontSize: "16px" }}>
                {loading
                  ? "Loading credit limits..."
                  : "No credit limits found. Run the credit limit SQL seed script."}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ManageRiderCreditLimits;
